/**
 * Web Push helpers — permission request, subscribe / unsubscribe, status.
 *
 * Permission UX rule: NEVER request permission without an explicit user click.
 * Browsers (especially iOS Safari + Chrome) demote sites that ask on page load.
 */
import api from "./api";

const SUGGEST_DISMISS_KEY = "synergy_push_dismissed_at";
const SUGGEST_COOLDOWN_DAYS = 7;

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  if (typeof window === "undefined") return false;
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function permissionState() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function currentSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function enablePush() {
  if (!isPushSupported()) throw new Error("Push not supported on this browser.");
  // Ask permission only inside the user-click handler.
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(permission === "denied"
      ? "You blocked notifications. Re-enable them in browser settings."
      : "Permission not granted.");
  }
  const reg = await navigator.serviceWorker.ready;
  // Fetch VAPID public key from backend
  const { data } = await api.get("/push/vapid-public-key");
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // If existing subscription uses a different key, drop it and re-subscribe.
    const existingKey = sub.options?.applicationServerKey;
    const incomingKey = urlBase64ToUint8Array(data.key);
    const sameKey = existingKey &&
      new Uint8Array(existingKey).every((b, i) => b === incomingKey[i]) &&
      existingKey.byteLength === incomingKey.length;
    if (!sameKey) {
      try { await sub.unsubscribe(); } catch (e) { console.warn("Push: stale unsubscribe failed", e); }
      sub = null;
    }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.key),
    });
  }
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    user_agent: navigator.userAgent,
  });
  return sub;
}

export async function disablePush() {
  const sub = await currentSubscription();
  if (sub) {
    try {
      await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } });
    } catch (e) { console.warn("Push: server unsubscribe failed", e); }
    try { await sub.unsubscribe(); } catch (e) { console.warn("Push: browser unsubscribe failed", e); }
  }
}

export async function sendTestPush() {
  return api.post("/push/test");
}

export function suggestionRecentlyDismissed() {
  const ts = Number(localStorage.getItem(SUGGEST_DISMISS_KEY) || 0);
  if (!ts) return false;
  return Date.now() - ts < SUGGEST_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
}

export function dismissSuggestion() {
  localStorage.setItem(SUGGEST_DISMISS_KEY, String(Date.now()));
}
